import { useState } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Eye, EyeSlash, ShieldCheck } from '@phosphor-icons/react';

const formatDateTime = (iso, locale) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(locale || 'fr-FR', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return '—'; }
};

const PasswordInput = ({ value, onChange, testId, ...props }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <Input
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        className="border border-border pr-10"
        data-testid={testId}
        {...props}
      />
      <button
        type="button"
        onClick={() => setVisible(!visible)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-text_muted hover:text-text_main"
        data-testid={`${testId}-toggle`}
      >
        {visible ? <EyeSlash size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );
};

/**
 * Security tab: change password + last login timestamp + 2FA placeholder.
 */
export const SecurityTab = ({
  currentPassword, setCurrentPassword,
  newPassword, setNewPassword,
  confirmPassword, setConfirmPassword,
  changingPw, onChangePassword,
  profile, language,
  t,
}) => (
  <div className="bg-white border border-border rounded-2xl p-6" data-testid="password-section">
    <h2 className="font-semibold font-heading mb-4">{t('settings.changePassword')}</h2>
    <div className="space-y-4">
      <div>
        <Label>{t('settings.currentPassword')}</Label>
        <PasswordInput
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          testId="current-pw-input"
        />
      </div>
      <div>
        <Label>{t('settings.newPassword')}</Label>
        <PasswordInput
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          testId="new-pw-input"
          minLength={8}
        />
      </div>
      <div>
        <Label>{t('auth.confirmPassword')}</Label>
        <PasswordInput
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          testId="confirm-pw-input"
          minLength={8}
        />
      </div>
      <Button
        onClick={onChangePassword}
        disabled={changingPw || !currentPassword || !newPassword}
        variant="primary"
        className="font-semibold px-8"
        data-testid="change-pw-btn"
      >
        {changingPw ? t('common.loading') : t('settings.updatePassword')}
      </Button>

      <div className="pt-3 border-t border-border space-y-1.5">
        <p className="text-xs text-text_muted" data-testid="last-login">
          {t('settings.lastLoginAt')}: <span className="font-medium text-text_main">{formatDateTime(profile?.last_login_at, language)}</span>
        </p>
        <p className="text-xs text-text_muted flex items-center gap-1" data-testid="two-factor-placeholder">
          <ShieldCheck size={13} /> {t('settings.twoFactorComingSoon')}
        </p>
      </div>
    </div>
  </div>
);
